import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";
import type { DirtLevel, HealthStatus } from "../domain/hallOps";

export type AppetiteLevel = "low" | "normal" | "high";
export type LeftoverLevel = "none" | "low" | "high";
export type TreatmentRoute = "bath" | "water" | "feed" | "oral" | "injection" | "topical" | "other";

export interface WorkerOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  tankId: string;
  performedAt: string;
  notes?: string;
}

export interface OperationBuilderOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
  entityIdSource?: OperationIdSource;
}

const entityIdSource: OperationIdSource = {
  next: () => crypto.randomUUID(),
};

function base(
  context: WorkerOperationContext,
  operationType: string,
  payload: Readonly<Record<string, unknown>>,
  options?: OperationBuilderOptions,
  baseVersion?: string,
): OfflineOperation {
  if (!context.actorId || !context.farmId || !context.deviceId || !context.tankId) {
    throw new Error("WORKER_OPERATION_CONTEXT_REQUIRED");
  }

  return createOfflineOperation(
    {
      operationType,
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: context.tankId,
      performedAt: context.performedAt,
      ...(baseVersion ? { baseVersion } : {}),
      payload: {
        tankId: context.tankId,
        ...(context.notes?.trim() ? { notes: context.notes.trim() } : {}),
        ...payload,
      },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

export function buildFeedingDayPrepareOperation(
  input: {
    actorId: string;
    farmId: string;
    deviceId: string;
    localDate: string;
    performedAt: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (
    !input.actorId ||
    !input.farmId ||
    !input.deviceId ||
    !/^\d{4}-\d{2}-\d{2}$/.test(input.localDate)
  ) {
    throw new Error("FEEDING_DAY_PREPARE_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "feeding.day_prepare",
      schemaVersion: 1,
      actorId: input.actorId,
      farmId: input.farmId,
      deviceId: input.deviceId,
      performedAt: input.performedAt,
      payload: { localDate: input.localDate },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

export function buildFeedingOperation(
  context: WorkerOperationContext,
  input: {
    roundTankId: string;
    feedProductId: string;
    amount: number;
    unit: string;
    appetiteLevel: AppetiteLevel;
    leftoverLevel: LeftoverLevel;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (
    !input.roundTankId ||
    !input.feedProductId ||
    !Number.isFinite(input.amount) ||
    input.amount <= 0
  ) {
    throw new Error("FEEDING_INPUT_INVALID");
  }

  const source = options?.entityIdSource ?? entityIdSource;

  return base(
    context,
    "feeding.record",
    {
      feedingEventId: source.next(),
      roundTankId: input.roundTankId,
      feedProductId: input.feedProductId,
      amount: input.amount,
      unit: input.unit.trim() || "g",
      appetiteLevel: input.appetiteLevel,
      leftoverLevel: input.leftoverLevel,
    },
    options,
  );
}

export interface WaterMeasurementInput {
  temperatureC?: number;
  ph?: number;
  tdsPpm?: number;
  ammoniaMgL?: number;
  nitriteMgL?: number;
  nitrateMgL?: number;
  ghDgh?: number;
  khDkh?: number;
  dissolvedOxygenMgL?: number;
}

export function buildWaterMeasurementOperation(
  context: WorkerOperationContext,
  input: WaterMeasurementInput,
  options?: OperationBuilderOptions,
): OfflineOperation {
  const values = Object.values(input).filter((value) => value !== undefined);
  if (values.length === 0 || values.some((value) => !Number.isFinite(value))) {
    throw new Error("WATER_MEASUREMENT_VALUE_REQUIRED");
  }

  return base(
    context,
    "hall.water_measurement",
    {
      details: {
        scopeKind: "tank",
        ...input,
      },
    },
    options,
  );
}

export function buildWaterChangeOperation(
  context: WorkerOperationContext,
  input: {
    changedPercent?: number;
    changedVolumeL?: number;
    tdsBefore?: number;
    tdsAfter?: number;
    temperatureBeforeC?: number;
    temperatureAfterC?: number;
    phBefore?: number;
    phAfter?: number;
    reason?: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (input.changedPercent === undefined && input.changedVolumeL === undefined) {
    throw new Error("WATER_CHANGE_AMOUNT_REQUIRED");
  }

  if (
    input.changedPercent !== undefined &&
    (!Number.isFinite(input.changedPercent) ||
      input.changedPercent <= 0 ||
      input.changedPercent > 100)
  ) {
    throw new Error("WATER_CHANGE_PERCENT_INVALID");
  }

  if (
    input.changedVolumeL !== undefined &&
    (!Number.isFinite(input.changedVolumeL) || input.changedVolumeL <= 0)
  ) {
    throw new Error("WATER_CHANGE_VOLUME_INVALID");
  }

  return base(
    context,
    "hall.water_change",
    {
      details: {
        scopeKind: "tank",
        ...input,
      },
    },
    options,
  );
}

export function buildSiphonOperation(
  context: WorkerOperationContext,
  input: {
    dirtLevelBefore: DirtLevel;
    dirtLevelAfter?: DirtLevel;
    siphonPercent?: 10 | 20 | 30 | 50 | 90 | 100;
    leftoverFoodSeen: boolean;
    abnormalFecesSeen: boolean;
    deadFishSeen: boolean;
    removedWaterL?: number;
    mortalityCount?: number;
    stockGroupId?: string;
    stockVersion?: number;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  const mortalityCount = input.mortalityCount ?? 0;

  if (mortalityCount < 0 || !Number.isInteger(mortalityCount)) {
    throw new Error("SIPHON_MORTALITY_COUNT_INVALID");
  }

  if (
    mortalityCount > 0 &&
    (!input.stockGroupId || !Number.isInteger(input.stockVersion) || (input.stockVersion ?? -1) < 0)
  ) {
    throw new Error("MORTALITY_STOCK_VERSION_REQUIRED");
  }

  return base(
    context,
    "hall.siphon",
    {
      ...(input.stockGroupId ? { stockGroupId: input.stockGroupId } : {}),
      ...(input.stockVersion === undefined ? {} : { expectedStockVersion: input.stockVersion }),
      details: {
        dirtLevelBefore: input.dirtLevelBefore,
        ...(input.dirtLevelAfter ? { dirtLevelAfter: input.dirtLevelAfter } : {}),
        ...(input.siphonPercent ? { siphonPercent: input.siphonPercent } : {}),
        leftoverFoodSeen: input.leftoverFoodSeen,
        abnormalFecesSeen: input.abnormalFecesSeen,
        deadFishSeen: input.deadFishSeen,
        ...(input.removedWaterL ? { removedWaterL: input.removedWaterL } : {}),
        ...(mortalityCount > 0 ? { mortalityCount } : {}),
      },
    },
    options,
    input.stockVersion === undefined ? undefined : String(input.stockVersion),
  );
}

export function buildHealthObservationOperation(
  context: WorkerOperationContext,
  input: {
    overallStatus: HealthStatus;
    swimmingStatus?: string;
    appetiteStatus?: string;
    appearanceStatus?: string;
    fecesStatus?: string;
    respirationStatus?: string;
    stockGroupId?: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  return base(
    context,
    "hall.health_observation",
    {
      ...(input.stockGroupId ? { stockGroupId: input.stockGroupId } : {}),
      details: {
        overallStatus: input.overallStatus,
        ...(input.swimmingStatus?.trim() ? { swimmingStatus: input.swimmingStatus.trim() } : {}),
        ...(input.appetiteStatus?.trim() ? { appetiteStatus: input.appetiteStatus.trim() } : {}),
        ...(input.appearanceStatus?.trim()
          ? { appearanceStatus: input.appearanceStatus.trim() }
          : {}),
        ...(input.fecesStatus?.trim() ? { fecesStatus: input.fecesStatus.trim() } : {}),
        ...(input.respirationStatus?.trim()
          ? { respirationStatus: input.respirationStatus.trim() }
          : {}),
      },
    },
    options,
  );
}

export function buildMortalityOperation(
  context: WorkerOperationContext,
  input: {
    deadCount: number;
    stockGroupId: string;
    stockVersion: number;
    cause?: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (!Number.isInteger(input.deadCount) || input.deadCount <= 0) {
    throw new Error("MORTALITY_COUNT_INVALID");
  }

  if (!input.stockGroupId || !Number.isInteger(input.stockVersion) || input.stockVersion < 0) {
    throw new Error("MORTALITY_STOCK_VERSION_REQUIRED");
  }

  return base(
    context,
    "hall.mortality",
    {
      stockGroupId: input.stockGroupId,
      expectedStockVersion: input.stockVersion,
      details: {
        deadCount: input.deadCount,
        ...(input.cause?.trim() ? { mortalityCause: input.cause.trim() } : {}),
      },
    },
    options,
    String(input.stockVersion),
  );
}

export function buildTreatmentOperation(
  context: WorkerOperationContext,
  input: {
    medicineName: string;
    route: TreatmentRoute;
    protocolId?: string;
    doseValue?: number;
    doseUnit?: string;
    durationDays?: number;
    outcome?: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (!input.medicineName.trim()) {
    throw new Error("TREATMENT_MEDICINE_REQUIRED");
  }

  return base(
    context,
    "hall.treatment",
    {
      details: {
        medicineName: input.medicineName.trim(),
        route: input.route,
        ...(input.protocolId ? { protocolId: input.protocolId } : {}),
        ...(input.doseValue ? { doseValue: input.doseValue } : {}),
        ...(input.doseUnit?.trim() ? { doseUnit: input.doseUnit.trim() } : {}),
        ...(input.durationDays ? { durationDays: input.durationDays } : {}),
        ...(input.outcome?.trim() ? { outcome: input.outcome.trim() } : {}),
        groupBath: false,
      },
    },
    options,
  );
}

export function buildTaskCompleteOperation(
  input: {
    actorId: string;
    farmId: string;
    deviceId: string;
    taskId: string;
    expectedVersion: number;
    performedAt: string;
    completionNotes?: string;
  },
  options?: OperationBuilderOptions,
): OfflineOperation {
  if (
    !input.actorId ||
    !input.farmId ||
    !input.deviceId ||
    !input.taskId ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 1
  ) {
    throw new Error("TASK_COMPLETE_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "task.complete",
      schemaVersion: 1,
      actorId: input.actorId,
      farmId: input.farmId,
      deviceId: input.deviceId,
      entityId: input.taskId,
      performedAt: input.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        taskId: input.taskId,
        expectedVersion: input.expectedVersion,
        ...(input.completionNotes?.trim() ? { completionNotes: input.completionNotes.trim() } : {}),
      },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}
